import React, { useState, useCallback, useEffect, useMemo } from "react";
import Select from "react-select";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import useThemeColor from "../../hooks/useThemeColor.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from "../staff/dropdownStyles.js";
import moment from "moment";
import swal from "sweetalert";
import "./UpdateCompany.css";

const iconBtnStyle = (color) => ({
  width: "32px",
  height: "32px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "6px",
  border: `1px solid ${color}`,
  color,
  padding: 0,
  background: "transparent",
  transition: "all 0.2s ease",
});

const isPendingPayment = (invoice) => {
  const status = (invoice?.paymentStatus || invoice?.PaymentStatus || "").toLowerCase();
  return status === "pending" || status === "partial";
};

/** Unpaid pending only — editable / deletable in SuperAdmin */
const isUnpaidPendingInvoice = (invoice) => {
  const payment = (invoice?.paymentStatus || invoice?.PaymentStatus || "").toLowerCase();
  const status = (invoice?.status || invoice?.Status || "").toLowerCase();
  const paid = parseFloat(invoice?.paidAmount ?? invoice?.PaidAmount ?? 0) || 0;
  return payment === "pending" && paid <= 0 && status !== "paid" && status !== "refunded" && status !== "cancelled";
};

const emptyCreateForm = () => ({
  startDate: moment().format("YYYY-MM-DD"),
  dueDate: moment().add(14, "days").format("YYYY-MM-DD"),
  notes: "",
  selectedPackages: [],
  customLines: [],
  isAutoRenewal: true,
  discountPercent: "",
  discountAmount: "",
  discountLabel: "",
});

function toMonths(duration) {
  const d = parseInt(duration, 10) || 1;
  if (d === 30 || d === 31) return 1;
  if (d === 365 || d === 366) return 12;
  if (d > 24) return Math.max(1, Math.round(d / 30));
  return d;
}

const CompanyInvoices = ({ CompID }) => {
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const [loading, setLoading] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [creating, setCreating] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [createForm, setCreateForm] = useState(emptyCreateForm);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_INVOICES_BY_COMPANY_ID, {
        params: { compID: CompID },
      });

      if (response.data.status && Array.isArray(response.data.data)) {
        setInvoices(response.data.data);
      } else {
        setInvoices([]);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setLoading(false);
      showErrorToast("Failed to load invoices");
    }
  }, [CompID]);

  const fetchPackages = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_PACKAGES_WITH_PRICES);
      if (response.data.status && Array.isArray(response.data.data)) {
        setPackages(response.data.data);
      } else {
        setPackages([]);
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      setPackages([]);
    }
  }, []);

  const packageOptions = useMemo(() => {
    return packages
      .filter((pkg) => pkg.pStatus !== false)
      .map((pkg) => {
        const prices = Array.isArray(pkg.prices) ? pkg.prices : [];
        const price = prices[0];
        if (!price) return null;
        const months = toMonths(price.duration);
        const amount = parseFloat(price.price || 0).toFixed(2);
        return {
          value: pkg.id,
          label: `${pkg.pName} — £${amount} / ${months} mo`,
          package: pkg,
          price,
        };
      })
      .filter(Boolean);
  }, [packages]);

  const createTotals = useMemo(() => {
    let net = 0;
    let vat = 0;
    (createForm.selectedPackages || []).forEach((opt) => {
      const price = parseFloat(opt.price?.price || 0);
      const vatPercent = parseFloat(opt.package?.pVatPercent ?? 0);
      net += price;
      vat += Math.round(price * (vatPercent / 100) * 100) / 100;
    });
    (createForm.customLines || []).forEach((line) => {
      const qty = Math.max(1, parseInt(line.quantity, 10) || 1);
      const lineNet = Math.round((parseFloat(line.netAmount) || 0) * qty * 100) / 100;
      const vatPercent = Math.min(100, Math.max(0, parseFloat(line.vatPercent) || 0));
      net += lineNet;
      vat += Math.round(lineNet * (vatPercent / 100) * 100) / 100;
    });
    net = Math.round(net * 100) / 100;
    vat = Math.round(vat * 100) / 100;
    const gross = Math.round((net + vat) * 100) / 100;
    const pct = Math.min(100, Math.max(0, parseFloat(createForm.discountPercent) || 0));
    let discount = 0;
    if (pct > 0) {
      discount = Math.round(gross * (pct / 100) * 100) / 100;
    } else {
      discount = Math.max(0, parseFloat(createForm.discountAmount) || 0);
    }
    if (discount > gross) discount = gross;
    return {
      net,
      vat,
      gross,
      discount,
      discountPercent: pct,
      total: Math.round((gross - discount) * 100) / 100,
    };
  }, [
    createForm.selectedPackages,
    createForm.customLines,
    createForm.discountPercent,
    createForm.discountAmount,
  ]);

  const openCreateModal = () => {
    setCreateForm(emptyCreateForm());
    fetchPackages();
    setShowCreate(true);
  };

  const closeCreateModal = () => {
    setShowCreate(false);
    setCreateForm(emptyCreateForm());
  };

  const handleCreateInvoice = async () => {
    const pkgs = createForm.selectedPackages || [];
    const customs = createForm.customLines || [];
    if (pkgs.length === 0 && customs.length === 0) {
      showErrorToast("Add at least one package or custom bill line");
      return;
    }
    for (const line of customs) {
      if (!(line.description || "").trim() || (parseFloat(line.netAmount) || 0) <= 0) {
        showErrorToast("Each custom bill needs a description and amount");
        return;
      }
    }

    try {
      setCreating(true);
      const payload = {
        CompID: parseInt(CompID, 10),
        StartDate: createForm.startDate || moment().format("YYYY-MM-DD"),
        IsAutoRenewal: createForm.isAutoRenewal !== false,
        PackageSelections: pkgs.map((p) => ({ PackageID: p.value })),
        CustomLines: customs.map((c) => ({
          Description: (c.description || "").trim(),
          NetAmount: parseFloat(c.netAmount) || 0,
          VatPercent: parseFloat(c.vatPercent) || 0,
          Quantity: Math.max(1, parseInt(c.quantity, 10) || 1),
        })),
        DiscountPercent: Math.min(
          100,
          Math.max(0, parseFloat(createForm.discountPercent) || 0)
        ),
        DiscountAmount:
          (parseFloat(createForm.discountPercent) || 0) > 0
            ? 0
            : Math.max(0, parseFloat(createForm.discountAmount) || 0),
        DiscountLabel: (createForm.discountLabel || "").trim(),
      };

      const response = await axiosInstance.post(
        APIs.POST_ASSIGN_PACKAGES_WITH_INVOICE,
        payload
      );
      if (response.data.status) {
        const data = response.data.data || {};
        const inv = data.invoice || data.Invoice || response.data.data;
        const invNo = inv?.invoiceNumber || inv?.InvoiceNumber || "";
        showSuccessToast(
          invNo
            ? `Invoice ${invNo} created (£${createTotals.total.toFixed(2)} incl. VAT)`
            : "Invoice created successfully"
        );
        closeCreateModal();
        await fetchInvoices();
      } else {
        showErrorToast(response.data.message || "Failed to create invoice");
      }
    } catch (error) {
      console.error(error);
      showErrorToast(
        error.response?.data?.message || "Failed to create invoice"
      );
    } finally {
      setCreating(false);
    }
  };

  const fetchInvoiceDetails = async (invoiceID) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_INVOICE_BY_ID, {
        params: { invoiceID: invoiceID },
      });

      if (response.data.status) {
        setSelectedInvoice(response.data.data);
        setShowDetails(true);
      } else {
        showErrorToast("Failed to load invoice details");
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      setLoading(false);
      showErrorToast("Failed to load invoice details");
    }
  };

  const handleMarkAsPaid = async (invoice) => {
    if (!invoice) return;

    const total = parseFloat(invoice.totalAmount ?? invoice.TotalAmount ?? 0);
    const paid = parseFloat(invoice.paidAmount ?? invoice.PaidAmount ?? 0);
    const remaining = Math.round((total - paid) * 100) / 100;
    if (remaining <= 0) {
      showErrorToast("Nothing left to pay on this invoice");
      return;
    }

    const confirmed = await swal({
      title: "Mark invoice as paid?",
      text: `Record £${remaining.toFixed(2)} as paid (incl. VAT). This will extend company access to the subscription end date.`,
      icon: "warning",
      buttons: true,
      dangerMode: false,
    });
    if (!confirmed) return;

    try {
      setMarkingPaid(true);
      const invoiceID = invoice.invoiceID ?? invoice.InvoiceID;
      const response = await axiosInstance.post(APIs.POST_MARK_INVOICE_AS_PAID, null, {
        params: {
          invoiceID,
          paidAmount: remaining,
          paymentMethod: "Manual",
          paymentReference: `SA-MARK-PAID-${invoiceID}`,
        },
      });

      if (response.data.status) {
        showSuccessToast("Invoice marked as paid. Company access updated.");
        await fetchInvoices();
        if (showDetails) {
          await fetchInvoiceDetails(invoiceID);
        }
      } else {
        showErrorToast(response.data.message || "Failed to mark invoice as paid");
      }
    } catch (error) {
      console.error(error);
      showErrorToast(
        error.response?.data?.message || "Failed to mark invoice as paid"
      );
    } finally {
      setMarkingPaid(false);
    }
  };

  const openEditModal = (invoice) => {
    if (!invoice || !isUnpaidPendingInvoice(invoice)) {
      showErrorToast("Only unpaid pending invoices can be updated");
      return;
    }
    const subtotal = parseFloat(invoice.subtotalAmount ?? invoice.SubtotalAmount ?? 0) || 0;
    const tax = parseFloat(invoice.taxAmount ?? invoice.TaxAmount ?? 0) || 0;
    const discount = parseFloat(invoice.discountAmount ?? invoice.DiscountAmount ?? 0) || 0;
    const discountPercent =
      parseFloat(invoice.discountPercent ?? invoice.DiscountPercent ?? 0) || 0;
    setEditForm({
      invoiceID: invoice.invoiceID ?? invoice.InvoiceID,
      invoiceNumber: invoice.invoiceNumber ?? invoice.InvoiceNumber ?? "",
      currency: invoice.currency ?? invoice.Currency ?? "GBP",
      status: invoice.status ?? invoice.Status ?? "Sent",
      dueDate: moment(invoice.dueDate ?? invoice.DueDate).format("YYYY-MM-DD"),
      notes: invoice.notes ?? invoice.Notes ?? "",
      subtotalAmount: subtotal,
      taxAmount: tax,
      discountAmount: discount,
      discountPercent,
      discountLabel: invoice.discountLabel ?? invoice.DiscountLabel ?? "",
      totalAmount:
        parseFloat(invoice.totalAmount ?? invoice.TotalAmount ?? 0) ||
        Math.round((subtotal + tax - discount) * 100) / 100,
    });
    setShowEdit(true);
  };

  const closeEditModal = () => {
    setShowEdit(false);
    setEditForm(null);
  };

  const editComputedTotal = useMemo(() => {
    if (!editForm) return 0;
    const subtotal = parseFloat(editForm.subtotalAmount) || 0;
    const tax = parseFloat(editForm.taxAmount) || 0;
    const gross = Math.round((subtotal + tax) * 100) / 100;
    const pct = Math.min(100, Math.max(0, parseFloat(editForm.discountPercent) || 0));
    let discount = 0;
    if (pct > 0) {
      discount = Math.round(gross * (pct / 100) * 100) / 100;
    } else {
      discount = Math.max(0, parseFloat(editForm.discountAmount) || 0);
    }
    if (discount > gross) discount = gross;
    return {
      discount,
      discountPercent: pct,
      total: Math.round((gross - discount) * 100) / 100,
      gross,
    };
  }, [editForm]);

  const handleSaveEdit = async () => {
    if (!editForm?.invoiceID) return;
    if (!editForm.dueDate) {
      showErrorToast("Due date is required");
      return;
    }

    try {
      setSavingEdit(true);
      const payload = {
        InvoiceID: editForm.invoiceID,
        DueDate: editForm.dueDate,
        SubtotalAmount: parseFloat(editForm.subtotalAmount) || 0,
        TaxAmount: parseFloat(editForm.taxAmount) || 0,
        DiscountAmount: editComputedTotal.discount,
        DiscountPercent: editComputedTotal.discountPercent,
        DiscountLabel: (editForm.discountLabel || "").trim(),
        TotalAmount: editComputedTotal.total,
        Status: editForm.status || "Sent",
        PaymentStatus: "Pending",
        Notes: editForm.notes || "",
      };

      const response = await axiosInstance.put(APIs.PUT_UPDATE_INVOICE, payload);
      if (response.data.status) {
        showSuccessToast(response.data.message || "Invoice updated");
        closeEditModal();
        await fetchInvoices();
        if (showDetails && selectedInvoice) {
          const id =
            selectedInvoice.invoiceID ??
            selectedInvoice.InvoiceID ??
            editForm.invoiceID;
          if (id === editForm.invoiceID) {
            await fetchInvoiceDetails(id);
          }
        }
      } else {
        showErrorToast(response.data.message || "Failed to update invoice");
      }
    } catch (error) {
      console.error(error);
      showErrorToast(
        error.response?.data?.message || "Failed to update invoice"
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeletePending = async (invoice) => {
    if (!invoice || !isUnpaidPendingInvoice(invoice)) {
      showErrorToast("Only unpaid pending invoices can be deleted");
      return;
    }

    const invoiceID = invoice.invoiceID ?? invoice.InvoiceID;
    const invNo = invoice.invoiceNumber ?? invoice.InvoiceNumber ?? invoiceID;
    const confirmed = await swal({
      title: "Delete pending invoice?",
      text: `Delete ${invNo}? Linked unpaid subscriptions will be cancelled. This cannot be undone.`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirmed) return;

    try {
      setDeletingId(invoiceID);
      const response = await axiosInstance.delete(APIs.DELETE_PENDING_INVOICE, {
        params: { invoiceID },
      });
      if (response.data.status) {
        showSuccessToast(response.data.message || "Invoice deleted");
        if (
          showDetails &&
          (selectedInvoice?.invoiceID ?? selectedInvoice?.InvoiceID) === invoiceID
        ) {
          closeDetails();
        }
        if (showEdit && editForm?.invoiceID === invoiceID) {
          closeEditModal();
        }
        await fetchInvoices();
      } else {
        showErrorToast(response.data.message || "Failed to delete invoice");
      }
    } catch (error) {
      console.error(error);
      showErrorToast(
        error.response?.data?.message || "Failed to delete invoice"
      );
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    if (CompID) {
      fetchInvoices();
    }
  }, [CompID, fetchInvoices]);

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedInvoice(null);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      Draft: { color: "secondary", text: "Draft", bg: "rgba(108, 117, 125, 0.12)" },
      Sent: { color: "info", text: "Sent", bg: "rgba(13, 202, 240, 0.15)" },
      Paid: { color: "success", text: "Paid", bg: "rgba(25, 135, 84, 0.12)" },
      PartiallyPaid: { color: "warning", text: "Partially Paid", bg: "rgba(255, 193, 7, 0.18)" },
      Overdue: { color: "danger", text: "Overdue", bg: "rgba(220, 53, 69, 0.12)" },
      Cancelled: { color: "dark", text: "Cancelled", bg: "rgba(33, 37, 41, 0.12)" },
    };
    const statusInfo = statusMap[status] || { color: "secondary", text: status, bg: "rgba(108, 117, 125, 0.12)" };
    return (
      <span className={`uc-badge-pill text-${statusInfo.color}`} style={{ background: statusInfo.bg }}>
        {statusInfo.text}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusMap = {
      Pending: { color: "warning", text: "Pending", bg: "rgba(255, 193, 7, 0.18)" },
      Paid: { color: "success", text: "Paid", bg: "rgba(25, 135, 84, 0.12)" },
      Partial: { color: "warning", text: "Partial", bg: "rgba(255, 193, 7, 0.18)" },
      Failed: { color: "danger", text: "Failed", bg: "rgba(220, 53, 69, 0.12)" },
      Refunded: { color: "info", text: "Refunded", bg: "rgba(13, 202, 240, 0.15)" },
    };
    const statusInfo = statusMap[status] || { color: "secondary", text: status, bg: "rgba(108, 117, 125, 0.12)" };
    return (
      <span className={`uc-badge-pill text-${statusInfo.color}`} style={{ background: statusInfo.bg }}>
        {statusInfo.text}
      </span>
    );
  };

  const actionButtonStyle = {
    backgroundColor: themeColor,
    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
    border: "none",
    color: "white",
    borderRadius: "8px",
    padding: "6px 12px",
    fontSize: "13px",
    fontWeight: "600",
  };

  if (loading && !showDetails) {
    return <Preloader />;
  }

  return (
    <>
      <CustomModal
        title={
          selectedInvoice
            ? `Invoice Details - ${selectedInvoice.invoiceNumber}`
            : "Invoice Details"
        }
        isOpen={showDetails && !!selectedInvoice}
        onClose={closeDetails}
        showButton={true}
        showCancelButton={true}
        showSubmitButton={false}
        isCentered={true}
        modalSize="modal-xl"
        maxHeight="85vh"
        minHeight="auto"
      >
        {selectedInvoice && (
          <>
            <div className="row mb-3">
              <div className="col-md-6">
                <h6 className="mb-3">Invoice Information</h6>
                <div className="uc-meta-grid">
                  <div className="uc-meta-item">
                    <span className="uc-meta-item__label">Invoice Number</span>
                    <div className="uc-meta-item__value">{selectedInvoice.invoiceNumber}</div>
                  </div>
                  <div className="uc-meta-item">
                    <span className="uc-meta-item__label">Invoice Date</span>
                    <div className="uc-meta-item__value">
                      {moment(selectedInvoice.invoiceDate).format("MMM DD, YYYY")}
                    </div>
                  </div>
                  <div className="uc-meta-item">
                    <span className="uc-meta-item__label">Due Date</span>
                    <div className="uc-meta-item__value">
                      {moment(selectedInvoice.dueDate).format("MMM DD, YYYY")}
                    </div>
                  </div>
                  <div className="uc-meta-item">
                    <span className="uc-meta-item__label">Status</span>
                    <div className="uc-meta-item__value">{getStatusBadge(selectedInvoice.status)}</div>
                  </div>
                  <div className="uc-meta-item">
                    <span className="uc-meta-item__label">Payment Status</span>
                    <div className="uc-meta-item__value">
                      {getPaymentStatusBadge(selectedInvoice.paymentStatus)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <h6 className="mb-3">Payment Information</h6>
                <div className="uc-meta-grid">
                  <div className="uc-meta-item">
                    <span className="uc-meta-item__label">Subtotal (ex-VAT)</span>
                    <div className="uc-meta-item__value">
                      {selectedInvoice.currency}{" "}
                      {parseFloat(selectedInvoice.subtotalAmount || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="uc-meta-item">
                    <span className="uc-meta-item__label">
                      Discount
                      {selectedInvoice.discountLabel || selectedInvoice.DiscountLabel
                        ? ` (${selectedInvoice.discountLabel || selectedInvoice.DiscountLabel})`
                        : ""}
                      {parseFloat(
                        selectedInvoice.discountPercent ||
                          selectedInvoice.DiscountPercent ||
                          0
                      ) > 0
                        ? ` ${parseFloat(
                            selectedInvoice.discountPercent ||
                              selectedInvoice.DiscountPercent ||
                              0
                          ).toFixed(2)}%`
                        : ""}
                    </span>
                    <div className="uc-meta-item__value text-danger">
                      -{selectedInvoice.currency}{" "}
                      {parseFloat(selectedInvoice.discountAmount || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="uc-meta-item">
                    <span className="uc-meta-item__label">VAT</span>
                    <div className="uc-meta-item__value">
                      {selectedInvoice.currency}{" "}
                      {parseFloat(selectedInvoice.taxAmount || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="uc-meta-item">
                    <span className="uc-meta-item__label">Total (incl. VAT)</span>
                    <div className="uc-meta-item__value text-primary fw-bold fs-5">
                      {selectedInvoice.currency}{" "}
                      {parseFloat(selectedInvoice.totalAmount || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="uc-meta-item">
                    <span className="uc-meta-item__label">Paid Amount</span>
                    <div className="uc-meta-item__value text-success">
                      {selectedInvoice.currency}{" "}
                      {parseFloat(selectedInvoice.paidAmount || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2 mt-3">
                  {isPendingPayment(selectedInvoice) && (
                    <button
                      type="button"
                      className="btn"
                      style={actionButtonStyle}
                      disabled={markingPaid}
                      onClick={() => handleMarkAsPaid(selectedInvoice)}
                    >
                      {markingPaid ? (
                        <>
                          <i className="fa fa-spinner fa-spin me-2" />
                          Marking...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-check me-2" />
                          Mark as Paid
                        </>
                      )}
                    </button>
                  )}
                  {isUnpaidPendingInvoice(selectedInvoice) && (
                    <>
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => openEditModal(selectedInvoice)}
                      >
                        <i className="fa fa-edit me-2" />
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        disabled={
                          deletingId ===
                          (selectedInvoice.invoiceID ?? selectedInvoice.InvoiceID)
                        }
                        onClick={() => handleDeletePending(selectedInvoice)}
                      >
                        <i className="fa fa-trash me-2" />
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {selectedInvoice.items && selectedInvoice.items.length > 0 && (
              <div className="row">
                <div className="col-12">
                  <h6 className="mb-3">Invoice Items</h6>
                  <div className="uc-table-wrap">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th>Type</th>
                          <th className="text-center">Quantity</th>
                          <th className="text-end">Unit Price</th>
                          <th className="text-end">VAT %</th>
                          <th className="text-end">VAT</th>
                          <th className="text-end">Total Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedInvoice.items.map((item) => {
                          const breakdown =
                            item.entitlementBreakdown ||
                            item.EntitlementBreakdown ||
                            [];
                          return (
                          <tr key={item.invoiceItemID}>
                            <td>
                              <div style={{ whiteSpace: "pre-line" }}>
                                {item.itemDescription}
                              </div>
                              {Array.isArray(breakdown) && breakdown.length > 0 && (
                                <ul className="small text-muted mb-0 mt-1 ps-3">
                                  {breakdown.map((ent, i) => {
                                    const title =
                                      (ent.entitlementTitle ||
                                        ent.EntitlementTitle ||
                                        "").trim() ||
                                      ent.entitlementType ||
                                      ent.EntitlementType ||
                                      "Entitlement";
                                    return (
                                    <li key={i}>
                                      {title}:{" "}
                                      {ent.quantity ?? ent.Quantity}{" "}
                                      {ent.unit || ent.Unit || ""} — £
                                      {parseFloat(
                                        ent.unitPrice ?? ent.UnitPrice ?? 0
                                      ).toFixed(2)}
                                    </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </td>
                            <td>
                              <span
                                className="uc-badge-pill text-info"
                                style={{ background: "rgba(13, 202, 240, 0.15)" }}
                              >
                                {item.itemType}
                              </span>
                            </td>
                            <td className="text-center">{item.quantity}</td>
                            <td className="text-end">
                              {selectedInvoice.currency}{" "}
                              {parseFloat(item.unitPrice || 0).toFixed(2)}
                            </td>
                            <td className="text-end">
                              {parseFloat(item.taxRate || 0).toFixed(2)}%
                            </td>
                            <td className="text-end">
                              {selectedInvoice.currency}{" "}
                              {parseFloat(item.taxAmount || 0).toFixed(2)}
                            </td>
                            <td className="text-end fw-bold">
                              {selectedInvoice.currency}{" "}
                              {parseFloat(item.totalPrice || 0).toFixed(2)}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </CustomModal>

      <CustomModal
        title="Create Multi-bill Invoice"
        isOpen={showCreate}
        onClose={closeCreateModal}
        showButton={true}
        showCancelButton={true}
        showSubmitButton={true}
        submitButtonText={creating ? "Creating..." : "Create Invoice"}
        cancelButtonText="Cancel"
        onSubmit={handleCreateInvoice}
        isCentered={true}
        modalSize="modal-xl"
        maxHeight="85vh"
        minHeight="auto"
        submitButtonDisabled={creating}
      >
        <div className="row g-3">
          <div className="col-md-8">
            <label className="form-label">Packages</label>
            <Select
              {...defaultSelectPortalProps}
              isMulti
              options={packageOptions}
              value={createForm.selectedPackages}
              onChange={(val) =>
                setCreateForm((prev) => ({ ...prev, selectedPackages: val || [] }))
              }
              placeholder="Select one or more packages..."
              styles={dropdownStyles}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Start date (subscriptions)</label>
            <input
              type="date"
              className="form-control"
              value={createForm.startDate}
              onChange={(e) =>
                setCreateForm((prev) => ({ ...prev, startDate: e.target.value }))
              }
            />
          </div>

          <div className="col-12 d-flex justify-content-between align-items-center">
            <label className="form-label mb-0">Custom customer bills</label>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() =>
                setCreateForm((prev) => ({
                  ...prev,
                  customLines: [
                    ...(prev.customLines || []),
                    { description: "", netAmount: "", vatPercent: "20", quantity: 1 },
                  ],
                }))
              }
            >
              <i className="fa fa-plus me-1" />
              Add bill line
            </button>
          </div>
          {(createForm.customLines || []).length > 0 && (
            <div className="col-12">
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Net (£)</th>
                      <th>VAT %</th>
                      <th>Qty</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {createForm.customLines.map((line, idx) => (
                      <tr key={idx}>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={line.description}
                            onChange={(e) => {
                              const value = e.target.value;
                              setCreateForm((prev) => ({
                                ...prev,
                                customLines: prev.customLines.map((r, i) =>
                                  i === idx ? { ...r, description: value } : r
                                ),
                              }));
                            }}
                          />
                        </td>
                        <td style={{ width: 110 }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            step="0.01"
                            min="0"
                            value={line.netAmount}
                            onChange={(e) => {
                              const value = e.target.value;
                              setCreateForm((prev) => ({
                                ...prev,
                                customLines: prev.customLines.map((r, i) =>
                                  i === idx ? { ...r, netAmount: value } : r
                                ),
                              }));
                            }}
                          />
                        </td>
                        <td style={{ width: 90 }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            step="0.01"
                            min="0"
                            max="100"
                            value={line.vatPercent}
                            onChange={(e) => {
                              const value = e.target.value;
                              setCreateForm((prev) => ({
                                ...prev,
                                customLines: prev.customLines.map((r, i) =>
                                  i === idx ? { ...r, vatPercent: value } : r
                                ),
                              }));
                            }}
                          />
                        </td>
                        <td style={{ width: 70 }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            min="1"
                            value={line.quantity}
                            onChange={(e) => {
                              const value = e.target.value;
                              setCreateForm((prev) => ({
                                ...prev,
                                customLines: prev.customLines.map((r, i) =>
                                  i === idx ? { ...r, quantity: value } : r
                                ),
                              }));
                            }}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() =>
                              setCreateForm((prev) => ({
                                ...prev,
                                customLines: prev.customLines.filter((_, i) => i !== idx),
                              }))
                            }
                          >
                            <i className="fa fa-trash" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="col-12">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="createAutoRenewal"
                checked={createForm.isAutoRenewal !== false}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    isAutoRenewal: e.target.checked,
                  }))
                }
              />
              <label className="form-check-label" htmlFor="createAutoRenewal">
                Auto renewal for package subscriptions
              </label>
            </div>
          </div>

          <div className="col-md-4">
            <label className="form-label">Discount label</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. Super Discount"
              value={createForm.discountLabel}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  discountLabel: e.target.value,
                }))
              }
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Discount %</label>
            <input
              type="number"
              className="form-control"
              min="0"
              max="100"
              step="0.01"
              placeholder="0"
              value={createForm.discountPercent}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  discountPercent: e.target.value,
                  discountAmount: e.target.value ? "" : prev.discountAmount,
                }))
              }
            />
            <small className="text-muted">% off subtotal + VAT</small>
          </div>
          <div className="col-md-4">
            <label className="form-label">Or fixed discount (£)</label>
            <input
              type="number"
              className="form-control"
              min="0"
              step="0.01"
              placeholder="0.00"
              disabled={(parseFloat(createForm.discountPercent) || 0) > 0}
              value={createForm.discountAmount}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  discountAmount: e.target.value,
                }))
              }
            />
            <small className="text-muted">Used when % is empty</small>
          </div>

          <div className="col-12">
            <div className="uc-meta-grid">
              <div className="uc-meta-item">
                <span className="uc-meta-item__label">Net</span>
                <div className="uc-meta-item__value">
                  £{createTotals.net.toFixed(2)}
                </div>
              </div>
              <div className="uc-meta-item">
                <span className="uc-meta-item__label">VAT</span>
                <div className="uc-meta-item__value">
                  £{createTotals.vat.toFixed(2)}
                </div>
              </div>
              <div className="uc-meta-item">
                <span className="uc-meta-item__label">
                  Discount
                  {createTotals.discountPercent > 0
                    ? ` (${createTotals.discountPercent}%)`
                    : ""}
                </span>
                <div className="uc-meta-item__value text-danger">
                  −£{createTotals.discount.toFixed(2)}
                </div>
              </div>
              <div className="uc-meta-item">
                <span className="uc-meta-item__label">Total payable</span>
                <div className="uc-meta-item__value text-success fw-bold">
                  £{createTotals.total.toFixed(2)}
                  {createTotals.discount > 0 ? (
                    <span className="text-muted text-decoration-line-through ms-2 small fw-normal">
                      £{createTotals.gross.toFixed(2)}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        title={
          editForm
            ? `Edit Invoice - ${editForm.invoiceNumber || editForm.invoiceID}`
            : "Edit Invoice"
        }
        isOpen={showEdit && !!editForm}
        onClose={closeEditModal}
        showButton={true}
        showCancelButton={true}
        showSubmitButton={true}
        submitButtonText={savingEdit ? "Saving..." : "Save Changes"}
        cancelButtonText="Cancel"
        onSubmit={handleSaveEdit}
        isCentered={true}
        modalSize="modal-lg"
        maxHeight="85vh"
        minHeight="auto"
        submitButtonDisabled={savingEdit}
      >
        {editForm && (
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Due date</label>
              <input
                type="date"
                className="form-control"
                value={editForm.dueDate}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, dueDate: e.target.value }))
                }
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Discount label</label>
              <input
                type="text"
                className="form-control"
                placeholder="Optional"
                value={editForm.discountLabel}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    discountLabel: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Subtotal (ex-VAT)</label>
              <input
                type="number"
                className="form-control"
                step="0.01"
                min="0"
                value={editForm.subtotalAmount}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    subtotalAmount: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">VAT amount</label>
              <input
                type="number"
                className="form-control"
                step="0.01"
                min="0"
                value={editForm.taxAmount}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    taxAmount: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Total payable</label>
              <input
                type="text"
                className="form-control fw-bold"
                readOnly
                value={`${editForm.currency} ${editComputedTotal.total.toFixed(2)}`}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Discount %</label>
              <input
                type="number"
                className="form-control"
                step="0.01"
                min="0"
                max="100"
                value={editForm.discountPercent}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    discountPercent: e.target.value,
                    discountAmount:
                      parseFloat(e.target.value) > 0 ? "" : prev.discountAmount,
                  }))
                }
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Discount £ (if no %)</label>
              <input
                type="number"
                className="form-control"
                step="0.01"
                min="0"
                disabled={(parseFloat(editForm.discountPercent) || 0) > 0}
                value={editForm.discountAmount}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    discountAmount: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Applied discount</label>
              <input
                type="text"
                className="form-control text-danger"
                readOnly
                value={`−${editForm.currency} ${editComputedTotal.discount.toFixed(2)}`}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Notes</label>
              <textarea
                className="form-control"
                rows={3}
                value={editForm.notes}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, notes: e.target.value }))
                }
              />
            </div>
            <div className="col-12">
              <p className="small text-muted mb-0">
                Only unpaid pending invoices can be edited. Line items are not
                changed here — adjust header amounts / discount / due date.
              </p>
            </div>
          </div>
        )}
      </CustomModal>

      <section className="uc-section uc-section--nested">
        <div className="uc-section__header d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h5>Company Invoices ({invoices.length})</h5>
            <p>Billing history for this company</p>
          </div>
          <button
            type="button"
            className="btn"
            style={actionButtonStyle}
            onClick={openCreateModal}
          >
            <i className="fa fa-plus me-2" />
            Create Invoice
          </button>
        </div>
        <div className="uc-section__body">
          {invoices.length === 0 ? (
            <div className="uc-empty">
              <i className="fa fa-file-invoice"></i>
              No invoices found for this company.
              <div className="mt-3">
                <button
                  type="button"
                  className="btn"
                  style={actionButtonStyle}
                  onClick={openCreateModal}
                >
                  <i className="fa fa-plus me-2" />
                  Create Invoice
                </button>
              </div>
            </div>
          ) : (
            <div className="uc-table-wrap">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Date</th>
                    <th>Due Date</th>
                    <th className="text-end">Subtotal</th>
                    <th className="text-end">VAT</th>
                    <th className="text-end">Total</th>
                    <th className="text-end">Paid</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Payment</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.invoiceID}>
                      <td>
                        <strong>{invoice.invoiceNumber}</strong>
                      </td>
                      <td>{moment(invoice.invoiceDate).format("MMM DD, YYYY")}</td>
                      <td>{moment(invoice.dueDate).format("MMM DD, YYYY")}</td>
                      <td className="text-end">
                        {invoice.currency} {parseFloat(invoice.subtotalAmount || 0).toFixed(2)}
                      </td>
                      <td className="text-end">
                        {invoice.currency} {parseFloat(invoice.taxAmount || 0).toFixed(2)}
                      </td>
                      <td className="text-end fw-bold">
                        {invoice.currency} {parseFloat(invoice.totalAmount || 0).toFixed(2)}
                      </td>
                      <td className="text-end text-success">
                        {invoice.currency} {parseFloat(invoice.paidAmount || 0).toFixed(2)}
                      </td>
                      <td className="text-center">{getStatusBadge(invoice.status)}</td>
                      <td className="text-center">
                        {getPaymentStatusBadge(invoice.paymentStatus)}
                      </td>
                      <td className="text-center">
                        <div className="d-inline-flex gap-1 align-items-center">
                          <button
                            type="button"
                            className="btn btn-sm uc-icon-btn"
                            onClick={() => fetchInvoiceDetails(invoice.invoiceID)}
                            title="View Details"
                            style={iconBtnStyle(themeColor)}
                          >
                            <i className="fa fa-eye" style={{ fontSize: "12px" }}></i>
                          </button>
                          {isUnpaidPendingInvoice(invoice) && (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm uc-icon-btn"
                                onClick={() => openEditModal(invoice)}
                                title="Edit pending invoice"
                                style={iconBtnStyle("#0d6efd")}
                              >
                                <i className="fa fa-edit" style={{ fontSize: "12px" }}></i>
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm uc-icon-btn"
                                onClick={() => handleDeletePending(invoice)}
                                title="Delete pending invoice"
                                disabled={deletingId === invoice.invoiceID}
                                style={iconBtnStyle("#dc3545")}
                              >
                                <i className="fa fa-trash" style={{ fontSize: "12px" }}></i>
                              </button>
                            </>
                          )}
                          {isPendingPayment(invoice) && (
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={actionButtonStyle}
                              disabled={markingPaid}
                              onClick={() => handleMarkAsPaid(invoice)}
                              title="Mark as Paid"
                            >
                              <i className="fa fa-check" style={{ fontSize: "12px" }}></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default CompanyInvoices;
